// Utility to manage recent property viewing activities

const getActivitiesKey = () => {
  try {
    const user = localStorage.getItem('user');
    if (user) {
      const userObj = JSON.parse(user);
      return `propertyActivities_${userObj._id || userObj.id}`;
    }
  } catch (error) {
    console.error('Error getting user ID:', error);
  }
  return 'propertyActivities'; // Fallback for non-logged-in users
};

// Save a new property view activity
export const savePropertyActivity = (propertyData, searchData = null) => {
  try {
    const key = getActivitiesKey();
    const activities = getPropertyActivities();
    
    // Helper to safely extract string values
    const getSafeString = (value, fallback = '') => {
      if (!value) return fallback;
      if (typeof value === 'string') return value;
      if (typeof value === 'number') return String(value);
      if (typeof value === 'object') {
        if (value.name) return String(value.name);
        if (value.city) return String(value.city);
        if (value.value) return String(value.value);
        return fallback;
      }
      return String(value);
    };

    // Create activity object with safe string conversions
    const newActivity = {
      id: Date.now(),
      type: 'property_view',
      propertyId: getSafeString(propertyData.id || propertyData._id),
      title: getSafeString(propertyData.name || propertyData.title, 'Unknown Property'),
      location: getSafeString(propertyData.city) || getSafeString(propertyData.location) || getSafeString(propertyData.address, 'Unknown Location'),
      rating: typeof propertyData.rating === 'number' ? propertyData.rating : 0,
      pricePerNight: typeof propertyData.pricePerNight === 'number' ? propertyData.pricePerNight : 0,
      currency: getSafeString(propertyData.currency, 'USD'),
      image: getSafeString(propertyData.images?.[0]) || getSafeString(propertyData.image) || getSafeString(propertyData.imageDetails?.url) || null,
      checkIn: searchData?.checkIn || null,
      checkOut: searchData?.checkOut || null,
      nights: searchData?.nights || null,
      guests: searchData?.guests || null,
      source: getSafeString(propertyData.source, ''),
      link: getSafeString(propertyData.link, ''),
      timestamp: new Date().toISOString(),
    };

    // Remove duplicates - check if same property was viewed recently
    const filteredActivities = activities.filter(activity => {
      return activity.propertyId !== newActivity.propertyId;
    });

    // Add new activity at the beginning (most recent first)
    const updatedActivities = [newActivity, ...filteredActivities].slice(0, 10); // Keep only latest 10

    // Save to localStorage
    localStorage.setItem(key, JSON.stringify(updatedActivities));
    
    // Dispatch custom event for real-time updates
    window.dispatchEvent(new CustomEvent('propertyActivityUpdated', {
      detail: updatedActivities
    }));

    return updatedActivities;
  } catch (error) {
    console.error('Error saving property activity:', error);
    return [];
  }
};

// Get all property activities
export const getPropertyActivities = () => {
  try {
    const key = getActivitiesKey();
    const stored = localStorage.getItem(key);
    if (stored) {
      const activities = JSON.parse(stored);
      // Sort by timestamp (newest first) and remove duplicates
      const uniqueActivities = [];
      const seen = new Set();
      
      activities.forEach(activity => {
        if (!seen.has(activity.propertyId)) {
          seen.add(activity.propertyId);
          uniqueActivities.push(activity);
        }
      });
      
      return uniqueActivities.sort((a, b) => 
        new Date(b.timestamp) - new Date(a.timestamp)
      );
    }
  } catch (error) {
    console.error('Error getting property activities:', error);
  }
  return [];
};

// Clear all property activities
export const clearPropertyActivities = () => {
  try {
    const key = getActivitiesKey();
    localStorage.removeItem(key);
    window.dispatchEvent(new CustomEvent('propertyActivityUpdated', {
      detail: []
    }));
  } catch (error) {
    console.error('Error clearing property activities:', error);
  }
};

// Remove a specific activity
export const removePropertyActivity = (activityId) => {
  try {
    const key = getActivitiesKey();
    const activities = getPropertyActivities();
    const filtered = activities.filter(activity => activity.id !== activityId);
    localStorage.setItem(key, JSON.stringify(filtered));
    
    window.dispatchEvent(new CustomEvent('propertyActivityUpdated', {
      detail: filtered
    }));
    
    return filtered;
  } catch (error) {
    console.error('Error removing property activity:', error);
    return [];
  }
};

